/**
 * Console + error ring buffer. Wires up monkey-patches and event listeners
 * on first call so the buffer starts collecting from widget mount, NOT from
 * the moment the user opens the panel — we want to see errors that already
 * happened earlier in their session.
 *
 * Captures:
 *   - console.error / console.warn calls
 *   - window 'error' (uncaught exceptions)
 *   - window 'unhandledrejection'
 *
 * Cap: latest 20 entries, each message clipped to 500 chars, each stack to
 * 4000 chars. Idempotent: calling init twice is a no-op.
 */

export type LogLevel = "error" | "warn" | "uncaught" | "unhandled";

export type LogEntry = {
  level: LogLevel;
  message: string;
  stack?: string;
  ts: number; // ms since epoch
};

const MAX_ENTRIES = 20;
const MAX_MESSAGE = 500;
const MAX_STACK = 4000;

const buffer: LogEntry[] = [];
let initialized = false;

export function initConsoleBuffer(): void {
  if (initialized || typeof window === "undefined") return;
  initialized = true;

  // console.error
  const origError = console.error.bind(console);
  console.error = (...args: unknown[]) => {
    push("error", args);
    origError(...(args as []));
  };

  // console.warn
  const origWarn = console.warn.bind(console);
  console.warn = (...args: unknown[]) => {
    push("warn", args);
    origWarn(...(args as []));
  };

  // uncaught exceptions
  window.addEventListener("error", (e: ErrorEvent) => {
    push("uncaught", [
      `${e.message}${e.filename ? ` @ ${e.filename}:${e.lineno}:${e.colno}` : ""}`,
      e.error instanceof Error ? e.error : undefined,
    ]);
  });

  // unhandled promise rejections
  window.addEventListener("unhandledrejection", (e: PromiseRejectionEvent) => {
    const reason = e.reason;
    push("unhandled", [reason instanceof Error ? reason : String(reason)]);
  });
}

function push(level: LogLevel, args: unknown[]): void {
  const parts: string[] = [];
  let stack: string | undefined;

  for (const a of args) {
    if (a instanceof Error) {
      parts.push(a.message);
      if (!stack && typeof a.stack === "string") {
        stack = a.stack.slice(0, MAX_STACK);
      }
    } else if (a === null || a === undefined) {
      parts.push(String(a));
    } else if (typeof a === "object") {
      try {
        parts.push(JSON.stringify(a));
      } catch {
        parts.push(String(a));
      }
    } else {
      parts.push(String(a));
    }
  }

  buffer.push({
    level,
    message: parts.join(" ").slice(0, MAX_MESSAGE),
    stack,
    ts: Date.now(),
  });
  if (buffer.length > MAX_ENTRIES) buffer.shift();
}

export function getConsoleBuffer(): LogEntry[] {
  return buffer.slice();
}

export function clearConsoleBuffer(): void {
  buffer.length = 0;
}
